import { useState } from 'react';
import useTrialStore from '@/stores/TrialStore';
import useUserStore from '@/stores/UserStore';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { setToken } from '@/lib/utils';
import { createTrialSubscription } from '@/hooks/trial';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

import PaymentElementSkeleton from './PaymentElementSkeleton';

export default function CheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const trial_id = useTrialStore((state) => state.trial_id);
  const setSetupIntentId = useTrialStore((state) => state.setSetupIntentId);
  const { setCurrentOnboardingStage, setNextOnboardingStage } = useUserStore(
    useShallow((state) => ({
      setCurrentOnboardingStage: state.setCurrentOnboardingStage,
      setNextOnboardingStage: state.setNextOnboardingStage,
    }))
  );

  const [message, setMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isElementReady, setIsElementReady] = useState(false);

  // Handle Payment Element ready event
  const handleElementReady = () => {
    setIsElementReady(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    if (!trial_id) {
      toast.error(
        'Invalid session. Please start from the onboarding signup page.'
      );
      navigate('/onboarding/signup');
      return;
    }

    setIsProcessing(true);
    setMessage(null);

    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required', // Only redirect if 3D Secure is required
      });

      if (error) {
        if (error.type === 'card_error' || error.type === 'validation_error') {
          setMessage(error.message);
        } else {
          setMessage('An unexpected error occurred.');
        }
        setIsProcessing(false);
        return;
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        // Payment setup successful, store the setup intent ID
        setSetupIntentId(setupIntent.id);

        // Create trial subscription
        try {
          const response = await createTrialSubscription({ trial_id });
          if (response.data) {
            setCurrentOnboardingStage(response.data.current_onboarding_stage);
            setNextOnboardingStage(response.data.next_onboarding_stage);
            setToken(response.data.access_token);

            // Navigate to completion page with subscription data
            navigate('/onboarding/completion', {
              state: {
                subscriptionData: response.data,
                setupIntentId: setupIntent.id,
              },
            });
          }
        } catch (subscriptionError) {
          console.error('Error creating subscription:', subscriptionError);
          toast.error(
            'Payment successful but failed to create subscription. Please contact support.'
          );
          setIsProcessing(false);
        }
      } else {
        setMessage('Payment setup was not completed successfully.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error during payment setup:', error);
      setMessage('An unexpected error occurred during payment setup.');
      setIsProcessing(false);
    }
  };

  return (
    <form id='payment-form' onSubmit={handleSubmit} className='space-y-6'>
      <div className='min-h-[200px] space-y-4'>
        {!isElementReady ? <PaymentElementSkeleton /> : null}
        <div
          className={`transition-opacity duration-300 ${
            isElementReady ? 'opacity-100' : 'absolute opacity-0'
          }`}
        >
          <PaymentElement
            id='payment-element'
            options={{
              layout: 'tabs',
            }}
            onReady={handleElementReady}
          />
        </div>
      </div>

      <Button
        type='submit'
        disabled={isProcessing || !stripe || !elements || !isElementReady}
        className='w-full'
        size='lg'
      >
        {isProcessing ? (
          <>
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            Processing...
          </>
        ) : (
          <>
            Complete Setup
            <ArrowRight className='ml-2 h-4 w-4' />
          </>
        )}
      </Button>

      {/* Show any error or success messages */}
      {message && (
        <Alert variant='destructive'>
          <AlertDescription>{message}</AlertDescription>
        </Alert>
      )}
    </form>
  );
}
